import { comoCampo } from "@/componentes/tesoreria/conversion"
import { aCentimos } from "@/componentes/tesoreria/dinero"
import type { MontoDivisa } from "@/componentes/ui/TarjetaKpi"

/**
 * Los indicadores de una cartera —por cobrar o por pagar— a partir de sus filas (16/10).
 *
 * ⚠ **Se cuentan en el navegador, y es deliberado**: la bandeja ya trae la cartera
 * entera y son decenas de filas, como el catálogo de gastos. El día que pase de unos
 * cientos, las cuentas se mueven al servidor como hizo la bandeja de órdenes.
 *
 * ⚠ **Por moneda, nunca sumadas** (`TarjetaKpi`): bolívares más dólares es una cifra en
 * una unidad que no existe. Y en céntimos enteros: en coma flotante se pierden.
 */
export type Partida = {
  monto: string
  aplicado: string
  moneda: string
  /** Fecha de vencimiento, o `null` si no tiene. */
  vence: string | null
  /** Saldada del todo: ya no queda nada. */
  saldada: boolean
  anulada: boolean
  /** El cliente o el proveedor, para contar a cuántos se les debe o nos deben. */
  tercero: number
}

export type Bolsa = { cuentas: number; montos: MontoDivisa[] }

export type Indicadores = {
  /** Con saldo: cuántas y cuánto falta, por moneda. */
  abiertas: Bolsa
  /** Con saldo y vencidas antes de `hoy`. */
  vencidas: Bolsa
  /** Lo ya cobrado o pagado, por moneda; `cuentas` son las saldadas. */
  aplicado: Bolsa
  /** A cuántos clientes o proveedores corresponden las abiertas. */
  terceros: number
}

function saldo(p: Partida): bigint {
  return aCentimos(p.monto) - aCentimos(p.aplicado)
}

export function abierta(p: Partida): boolean {
  return !p.anulada && !p.saldada && saldo(p) > 0n
}

export function vencida(p: Partida, hoy: string): boolean {
  return abierta(p) && p.vence !== null && p.vence < hoy
}

function bolsa(partidas: readonly Partida[], cuanto: (p: Partida) => bigint): Bolsa {
  const porMoneda = new Map<string, bigint>()
  for (const p of partidas) porMoneda.set(p.moneda, (porMoneda.get(p.moneda) ?? 0n) + cuanto(p))
  return {
    cuentas: partidas.length,
    montos: [...porMoneda]
      .filter(([, c]) => c !== 0n)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([divisa, c]) => ({ divisa, monto: comoCampo(c) })),
  }
}

export function indicadores(partidas: readonly Partida[], hoy: string): Indicadores {
  const abiertas = partidas.filter(abierta)
  const vivas = partidas.filter((p) => !p.anulada)
  const aplicado = bolsa(vivas, (p) => aCentimos(p.aplicado))
  return {
    abiertas: bolsa(abiertas, saldo),
    vencidas: bolsa(
      partidas.filter((p) => vencida(p, hoy)),
      saldo,
    ),
    aplicado: { ...aplicado, cuentas: vivas.filter((p) => p.saldada).length },
    terceros: new Set(abiertas.map((p) => p.tercero)).size,
  }
}

/**
 * Los tramos de la antigüedad de saldos (28/10, requerimiento de Andrea, §4.3 y «qué vence
 * pronto» del §5): lo que vence en 7, 15, 30 o más días, lo vencido por días de mora y lo
 * que no tiene fecha. UNA tabla para las dos preguntas, sobre las MISMAS partidas que los
 * indicadores de arriba, así que suma igual que «Por cobrar» o «Por pagar».
 */
export const TRAMOS = [
  { clave: "EN_7", etiqueta: "Vence en 7 días o menos", vencido: false },
  { clave: "EN_15", etiqueta: "Vence en 8 a 15 días", vencido: false },
  { clave: "EN_30", etiqueta: "Vence en 16 a 30 días", vencido: false },
  { clave: "MAS_30", etiqueta: "Vence en más de 30 días", vencido: false },
  { clave: "MORA_30", etiqueta: "Vencida hace 1 a 30 días", vencido: true },
  { clave: "MORA_60", etiqueta: "Vencida hace 31 a 60 días", vencido: true },
  { clave: "MORA_90", etiqueta: "Vencida hace 61 a 90 días", vencido: true },
  { clave: "MORA_MAS", etiqueta: "Vencida hace más de 90 días", vencido: true },
  { clave: "SIN_FECHA", etiqueta: "Sin fecha de vencimiento", vencido: false },
] as const

export type ClaveTramo = (typeof TRAMOS)[number]["clave"]

/** Días de `desde` a `hasta` (`YYYY-MM-DD`), sin pasar por la hora local. */
function diasEntre(desde: string, hasta: string): number {
  const dia = (iso: string) => {
    const [a = 1970, m = 1, d = 1] = iso.slice(0, 10).split("-").map(Number)
    return Date.UTC(a, m - 1, d) / 86_400_000
  }
  return dia(hasta) - dia(desde)
}

/** El tramo de una cuenta. El día del vencimiento todavía no es mora (igual que `vencida`). */
export function tramoDe(vence: string | null, hoy: string): ClaveTramo {
  if (!vence) return "SIN_FECHA"
  const mora = diasEntre(vence, hoy)
  if (mora <= 0) {
    const faltan = -mora
    return faltan <= 7 ? "EN_7" : faltan <= 15 ? "EN_15" : faltan <= 30 ? "EN_30" : "MAS_30"
  }
  return mora <= 30 ? "MORA_30" : mora <= 60 ? "MORA_60" : mora <= 90 ? "MORA_90" : "MORA_MAS"
}

/** Las cuentas abiertas repartidas por tramo, con su saldo por moneda. */
export function antiguedad(partidas: readonly Partida[], hoy: string): Record<ClaveTramo, Bolsa> {
  const abiertas = partidas.filter(abierta)
  return Object.fromEntries(
    TRAMOS.map((t) => [
      t.clave,
      bolsa(
        abiertas.filter((p) => tramoDe(p.vence, hoy) === t.clave),
        saldo,
      ),
    ]),
  ) as Record<ClaveTramo, Bolsa>
}
